'use client';

import React, { useState, useMemo, useEffect } from 'react';
import PromptCard from '@/components/PromptCard';
import SearchBar from '@/components/SearchBar';
import { Grid3X3, Rows3, Sparkles, X } from 'lucide-react';
import { searchPrompts } from '@/data/prompts';
import { categories } from '@/data/categories';
import type { Prompt } from '@/data/prompts';

interface AllPromptsViewProps {
  prompts: Prompt[];
}

const firstCategorySlug = categories.length > 0 ? categories[0].slug : 'all';

export default function AllPromptsView({ prompts }: AllPromptsViewProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(firstCategorySlug);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const search = params.get('search');
    const category = params.get('category');
    if (search) setSearchQuery(search);
    if (category) setActiveCategory(category);
    setInitialized(true);
  }, [initialized]);

  const filtered = useMemo(() => {
    let result = prompts;
    if (searchQuery.trim().length >= 2) {
      result = searchPrompts(searchQuery);
    }
    if (activeCategory !== 'all') {
      result = result.filter((p) => p.categorySlugs.includes(activeCategory));
    }
    return result;
  }, [prompts, searchQuery, activeCategory]);

  const hasActiveFilters = activeCategory !== 'all' || searchQuery.trim().length >= 2;

  const clearFilters = () => {
    setSearchQuery('');
    setActiveCategory(firstCategorySlug);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-3">
          Browse AI Image Prompts
        </h1>
        <p className="text-muted-foreground text-base max-w-2xl">
          {prompts.length}+ curated prompts for Midjourney, DALL·E 3, and Stable Diffusion — organized by style and category.
        </p>
      </div>

      {/* Search Bar */}
      <div className="sticky top-16 bg-white/90 backdrop-blur-sm py-4 z-30 border-b border-border -mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-10 px-4 sm:px-6 lg:px-8 xl:px-10 mb-8">
        <div className="flex gap-3 items-center">
          <div className="flex-1">
            <SearchBar
              variant="inline"
              placeholder="Search prompts by keyword, style, or subject..."
            />
          </div>
          <div className="hidden sm:flex items-center gap-1 border border-border rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-accent text-primary' : 'text-muted-foreground hover:bg-muted'}`}
              aria-label="Grid view"
            >
              <Grid3X3 size={15} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-accent text-primary' : 'text-muted-foreground hover:bg-muted'}`}
              aria-label="List view"
            >
              <Rows3 size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-muted-foreground">
          {filtered.length === prompts.length
            ? `Showing all ${prompts.length} prompts`
            : `${filtered.length} prompts found`}
          {searchQuery && ` for "${searchQuery}"`}
        </p>
        {hasActiveFilters && (
          <button onClick={clearFilters} className="text-xs text-primary hover:text-primary-dark font-medium flex items-center gap-1">
            <X size={12} />
            Clear filters
          </button>
        )}
      </div>

      {/* Prompts Grid */}
      {filtered.length > 0 ? (
        <div className={
          viewMode === 'grid' ?'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-5' :'space-y-3'
        }>
          {filtered.map((prompt) => (
            <PromptCard
              key={`all-prompt-${prompt.id}`}
              prompt={prompt}
              variant={viewMode === 'list' ? 'compact' : 'default'}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-muted/30 rounded-2xl border border-border">
          <Sparkles size={40} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-bold text-foreground mb-2">No prompts found</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
            No prompts match your current search and filter combination. Try adjusting your filters or search term.
          </p>
          <button onClick={clearFilters} className="btn-primary text-sm">
            Clear Filters & Browse All
          </button>
        </div>
      )}
    </div>
  );
}